import { useMemo, useState } from 'react';
import { useModalStore } from '../../store/modalStore';
import { useToastStore } from '../../store/toastStore';
import { useLists } from '../../hooks/useLists';
import { useBooks, useSearchBooks } from '../../hooks/useBooks';
import { useUpdateSavedResponseScope } from '../../hooks/useSavedResponses';
import type { SavedChatResponse } from '../../api/savedResponses';

interface RescopeSavedResponseModalData {
  savedResponse: SavedChatResponse;
}

type ScopeTab = 'book' | 'collection' | 'library';

export function RescopeSavedResponseModal() {
  const { modalType, modalData, closeModal } = useModalStore();
  const isOpen = modalType === 'rescopeSavedResponse';
  const data = modalData as RescopeSavedResponseModalData | null;

  const [tab, setTab] = useState<ScopeTab>('book');
  const [searchQuery, setSearchQuery] = useState('');
  const updateScope = useUpdateSavedResponseScope();
  const addToast = useToastStore((s) => s.addToast);

  const { data: allBooks = [] } = useBooks({ limit: 50 });
  const { data: searchedBooks = [] } = useSearchBooks({ q: searchQuery, limit: 50 });
  const { data: allLists = [] } = useLists();

  const books = searchQuery.trim() ? searchedBooks : allBooks;
  const filteredLists = useMemo(() => {
    if (!searchQuery.trim()) return allLists;
    const q = searchQuery.toLowerCase();
    return allLists.filter((l) => l.name.toLowerCase().includes(q));
  }, [allLists, searchQuery]);

  if (!isOpen || !data) return null;

  const { savedResponse } = data;

  const moveTo = async (bookId: number | null, listId: number | null) => {
    try {
      await updateScope.mutateAsync({ id: savedResponse.id, input: { book_id: bookId, list_id: listId } });
      addToast('Saved response moved', 'success');
      closeModal();
    } catch {
      addToast('Failed to move saved response', 'error');
    }
  };

  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal-content rescope-saved-response-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Move Saved Response</h2>
          <button onClick={closeModal} className="btn-close" type="button">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="scope-tabs">
            <button
              type="button"
              className={`scope-tab ${tab === 'book' ? 'scope-tab-active' : ''}`}
              onClick={() => setTab('book')}
            >
              Book
            </button>
            <button
              type="button"
              className={`scope-tab ${tab === 'collection' ? 'scope-tab-active' : ''}`}
              onClick={() => setTab('collection')}
            >
              Collection
            </button>
            <button
              type="button"
              className={`scope-tab ${tab === 'library' ? 'scope-tab-active' : ''}`}
              onClick={() => setTab('library')}
            >
              Library-wide
            </button>
          </div>

          {tab === 'library' && (
            <div className="scope-library-confirm">
              <p>Move this saved response to library-wide (no book or collection).</p>
              <button
                type="button"
                className="btn btn-primary"
                disabled={updateScope.isPending}
                onClick={() => void moveTo(null, null)}
              >
                {updateScope.isPending ? 'Moving...' : 'Move to library-wide'}
              </button>
            </div>
          )}

          {(tab === 'book' || tab === 'collection') && (
            <div className="form-group">
              <label htmlFor="rescope-search">{tab === 'book' ? 'Search books' : 'Search collections'}</label>
              <input
                id="rescope-search"
                type="text"
                placeholder="Type to filter..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
          )}

          {tab === 'book' && (
            <div className="collection-list">
              {books.length === 0 ? (
                <div className="empty-state">No books found</div>
              ) : (
                books.map((book) => (
                  <button
                    key={book.id}
                    type="button"
                    className="collection-item"
                    disabled={updateScope.isPending}
                    onClick={() => void moveTo(book.id, null)}
                  >
                    <div className="collection-item-content">
                      <div className="collection-item-name">{book.title}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {tab === 'collection' && (
            <div className="collection-list">
              {filteredLists.length === 0 ? (
                <div className="empty-state">No collections found</div>
              ) : (
                filteredLists.map((list) => (
                  <button
                    key={list.id}
                    type="button"
                    className="collection-item"
                    disabled={updateScope.isPending}
                    onClick={() => void moveTo(null, list.id)}
                  >
                    <div className="collection-item-content">
                      <div className="collection-item-name">{list.name}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={closeModal} className="btn btn-secondary" type="button">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
